import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  LISTENER_WEEKDAYS,
  copyListenerHoursToAllDays,
  copyListenerHoursToWeekdays,
  listenerHoursRangeLabel,
  listenerHoursSourceDay,
  daysFromListenerPlan,
  entriesFromListenerDays,
  listenerHourOptions,
  listenerTimeZoneLabel,
  nextListenerSlot,
  validateListenerHours,
} from '../../util/listenerHours';
import {
  dayOffWhenFromExceptions,
  formatDayOffWhen,
  oneOffDayOffsFromExceptions,
} from '../../util/changeOneDay';

import ChangeOneDayEditor from '../ChangeOneDayEditor/ChangeOneDayEditor';

import css from './ListenerHoursEditor.module.css';

const DAY_NAMES = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
};

const CopyIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
    <rect
      x="7"
      y="3"
      width="10"
      height="12"
      rx="2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <rect
      x="3"
      y="6"
      width="10"
      height="12"
      rx="2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
    />
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
    <path
      d="M5 6h10M8 6V4h4v2M7 6l.6 10h4.8L13 6"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CalendarOffIcon = () => (
  <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
    <rect
      x="3"
      y="4.5"
      width="14"
      height="12.5"
      rx="2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <path
      d="M3 8.5h14M7 3v3M13 3v3"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <path
      d="M8 12.2l4 4M12 12.2l-4 4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

/** Checked weekly-repeat note. Visual only — the availability plan is already weekly. */
const WeeklyRepeatNote = ({ placement }) => (
  <div className={css.weeklyRepeat} data-testid="listener-hours-repeats" data-placement={placement}>
    <span className={css.weeklyCheck} aria-hidden="true">
      ✓
    </span>
    <div>
      <p className={css.weeklyTitle}>
        <FormattedMessage id="ListenerCalendar.repeatsWeekly" />
      </p>
      <p className={css.weeklyNote}>
        <FormattedMessage id="ListenerCalendar.repeatsWeeklyNote" />
      </p>
    </div>
  </div>
);

/**
 * Listener weekly hours editor. Saves the listing availabilityPlan only.
 *
 * @param {Object} props
 * @returns {JSX.Element}
 */
const ListenerHoursEditor = props => {
  const {
    initialPlan,
    timezone: listingTimezone,
    onSave,
    inProgress = false,
    error = null,
    saved = false,
    headingTag = 'h2',
    saveMessageId = 'ListenerCalendar.saveSchedule',
    savedMessageId = 'ListenerCalendar.saved',
    saveTone = 'pink',
    onSaveException,
    exceptionInProgress = false,
    exceptionError = null,
    dayOffs = [],
    onDeleteDayOff,
    dayOffDeleteId = null,
    simple = false,
  } = props;
  const TitleTag = headingTag === 'h1' ? 'h1' : 'h2';
  const intl = useIntl();
  const startOptions = useMemo(() => listenerHourOptions(false), []);
  const endOptions = useMemo(() => listenerHourOptions(true), []);
  const zone =
    (typeof listingTimezone === 'string' && listingTimezone) || initialPlan?.timezone || '';
  const zoneName = listenerTimeZoneLabel(zone);
  const [days, setDays] = useState(() => daysFromListenerPlan(initialPlan));
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyToast, setCopyToast] = useState(null);
  const [dayOffOpen, setDayOffOpen] = useState(false);
  const [dayOffToast, setDayOffToast] = useState(null);
  const [localError, setLocalError] = useState(null);
  const [dirty, setDirty] = useState(false);

  const markDirty = () => setDirty(true);

  const setDay = (day, patch) => {
    setDays(prev => ({ ...prev, [day]: { ...prev[day], ...patch } }));
    setLocalError(null);
    markDirty();
  };

  const addSlot = day => {
    setDays(prev => {
      const row = prev[day] || { on: false, slots: [] };
      const nextSlot = { id: `new-${day}-${row.slots.length}`, ...nextListenerSlot(row.slots) };
      return {
        ...prev,
        [day]: { on: true, slots: [...row.slots, nextSlot] },
      };
    });
    setLocalError(null);
    markDirty();
  };

  const removeSlot = (day, slotId) => {
    setDays(prev => {
      const row = prev[day];
      const slots = (row?.slots || []).filter(slot => slot.id !== slotId);
      return {
        ...prev,
        [day]: { on: slots.length > 0, slots },
      };
    });
    setLocalError(null);
    markDirty();
  };

  const changeSlot = (day, slotId, patch) => {
    setDays(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        slots: prev[day].slots.map(slot => (slot.id === slotId ? { ...slot, ...patch } : slot)),
      },
    }));
    setLocalError(null);
    markDirty();
  };

  const toggleDay = (day, on) => {
    setDays(prev => {
      const row = prev[day] || { on: false, slots: [] };
      if (on) {
        return {
          ...prev,
          [day]: {
            on: true,
            slots: row.slots.length ? row.slots : [{ id: `new-${day}-0`, ...nextListenerSlot([]) }],
          },
        };
      }
      return { ...prev, [day]: { on: false, slots: [] } };
    });
    setLocalError(null);
    markDirty();
  };

  useEffect(() => {
    if (!dirty) {
      return undefined;
    }
    const message = intl.formatMessage({ id: 'ListenerCalendar.unsavedWarning' });
    const onBeforeUnload = event => {
      event.preventDefault();
      event.returnValue = message;
      return message;
    };
    const onClick = event => {
      const link = event.target.closest('a[href]');
      if (!link || link.target === '_blank' || event.defaultPrevented) {
        return;
      }
      if (!window.confirm(message)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('click', onClick, true);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('click', onClick, true);
    };
  }, [dirty, intl]);

  const onSubmit = event => {
    event.preventDefault();
    if (inProgress || typeof onSave !== 'function') {
      return;
    }
    const check = validateListenerHours(days);
    if (!check.ok) {
      const messageId =
        check.reason === 'overlap' || check.reason === 'duplicate'
          ? 'ListenerCalendar.overlap'
          : 'ListenerCalendar.endBeforeStart';
      setLocalError(intl.formatMessage({ id: messageId }));
      return;
    }
    setLocalError(null);
    onSave({
      timezone: zone,
      entries: entriesFromListenerDays(days),
    });
    setDirty(false);
  };

  useEffect(() => {
    if (saved) {
      setDirty(false);
    }
  }, [saved]);

  const confirmCopy = weekdaysOnly => {
    setDays(prev =>
      weekdaysOnly ? copyListenerHoursToWeekdays(prev) : copyListenerHoursToAllDays(prev)
    );
    setCopyOpen(false);
    setLocalError(null);
    setCopyToast(weekdaysOnly ? 'weekdays' : 'all');
    markDirty();
  };

  const copySourceDay = listenerHoursSourceDay(days);
  const copyRange = listenerHoursRangeLabel(days, copySourceDay);
  const copyDayName = DAY_NAMES[copySourceDay] || 'Monday';

  const displayError = localError || error;

  return (
    <div className={css.root}>
      <form
        className={css.form}
        onSubmit={onSubmit}
        data-testid="listener-hours-editor"
        data-timezone={zone}
        data-timezone-label={zoneName}
      >
        <div className={css.header}>
          <div className={css.headingBlock}>
            <TitleTag id="listener-calendar-heading" className={css.title}>
              {simple ? (
                <FormattedMessage id="ListenerCalendar.titleSimple" defaultMessage="Work schedule" />
              ) : (
                <FormattedMessage id="ListenerCalendar.title" />
              )}
            </TitleTag>
            <p className={css.lede}>
              {simple ? (
                <FormattedMessage
                  id="ListenerCalendar.ledeSimple"
                  defaultMessage="Your work schedule. Turn days on and set hours — Save when you’re done."
                />
              ) : (
                <FormattedMessage id="ListenerCalendar.lede" />
              )}
            </p>
          </div>
          <button
            type="button"
            className={css.copy}
            data-testid="listener-hours-copy"
            data-copy-day={copyDayName}
            onClick={() => setCopyOpen(true)}
          >
            <CopyIcon />
            <FormattedMessage
              id="ListenerCalendar.copyToAllDays"
              values={{ day: copyDayName }}
            />
          </button>
        </div>

        <p className={css.timezone} data-testid="listener-hours-timezone">
          <FormattedMessage id="ListenerCalendar.timesShownIn" values={{ timezone: zoneName }} />
        </p>
        <section className={css.weeklyBlock} data-testid="listener-hours-weekly">
          {!simple ? <WeeklyRepeatNote placement="top" /> : null}

          {displayError ? (
            <p className={css.error} role="alert">
              {displayError}
            </p>
          ) : null}
          {saved && !displayError ? (
            <p className={css.toast} role="status" data-testid="listener-hours-saved">
              <FormattedMessage id={savedMessageId} />
            </p>
          ) : null}
          {copyToast && !displayError ? (
            <p className={css.toast} role="status" data-testid="listener-hours-copy-toast">
              <FormattedMessage
                id={
                  copyToast === 'weekdays'
                    ? 'ListenerCalendar.copyToastWeekdays'
                    : 'ListenerCalendar.copyToast'
                }
              />
            </p>
          ) : null}

          <ol className={css.days}>
            {LISTENER_WEEKDAYS.map(day => {
              const row = days[day];
              const on = !!row?.on;
              const slots = on && row.slots.length ? row.slots : [];
              const dayName = DAY_NAMES[day];
              return (
                <li
                  key={day}
                  className={classNames(css.day, { [css.dayOff]: !on })}
                  data-testid={`listener-hours-day-${day}`}
                  data-on={on ? 'true' : 'false'}
                  data-slot-count={slots.length}
                >
                  <div className={css.dayLead}>
                    <span className={css.dayName}>{dayName}</span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={on}
                      aria-label={intl.formatMessage(
                        { id: 'ListenerCalendar.toggleDay' },
                        { day: dayName }
                      )}
                      className={classNames(css.switch, { [css.switchOn]: on })}
                      onClick={() => toggleDay(day, !on)}
                    >
                      <span className={css.switchThumb} />
                    </button>
                  </div>
                  <div className={css.dayBody}>
                    {!on ? (
                      <div className={css.offRow}>
                        <span className={css.notAvailable}>
                          <FormattedMessage id="ListenerCalendar.notAvailable" />
                        </span>
                        <button
                          type="button"
                          className={css.add}
                          data-testid={`listener-hours-add-${day}`}
                          onClick={() => addSlot(day)}
                        >
                          <FormattedMessage id="ListenerCalendar.addSlot" />
                        </button>
                      </div>
                    ) : (
                      slots.map((slot, index) => (
                        <div key={slot.id} className={css.slot} data-slot-index={index}>
                          <div className={css.times}>
                            <select
                              className={css.select}
                              value={slot.startTime}
                              aria-label={`${dayName} start ${index + 1}`}
                              onChange={event =>
                                changeSlot(day, slot.id, { startTime: event.target.value })
                              }
                              onInput={event =>
                                changeSlot(day, slot.id, { startTime: event.target.value })
                              }
                            >
                              {startOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            <select
                              className={css.select}
                              value={slot.endTime}
                              aria-label={`${dayName} end ${index + 1}`}
                              onChange={event =>
                                changeSlot(day, slot.id, { endTime: event.target.value })
                              }
                              onInput={event =>
                                changeSlot(day, slot.id, { endTime: event.target.value })
                              }
                            >
                              {endOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className={css.slotActions}>
                            {index === slots.length - 1 ? (
                              <button
                                type="button"
                                className={css.add}
                                data-testid={`listener-hours-add-${day}`}
                                onClick={() => addSlot(day)}
                              >
                                <FormattedMessage id="ListenerCalendar.addAnotherSlot" />
                              </button>
                            ) : (
                              <span className={css.addSpacer} />
                            )}
                            <button
                              type="button"
                              className={css.remove}
                              aria-label={intl.formatMessage(
                                { id: 'ListenerCalendar.removeSlot' },
                                { day: dayName }
                              )}
                              onClick={() => removeSlot(day, slot.id)}
                            >
                              <TrashIcon />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </section>

        {!simple ? (
        <section className={css.dayOffSection} data-testid="listener-hours-day-off-section">
          <h3 className={css.dayOffTitle}>
            <FormattedMessage id="ListenerCalendar.dayOffsTitle" />
          </h3>
          <p className={css.dayOffNote}>
            <FormattedMessage id="ListenerCalendar.dayOffsNote" />
          </p>
          {dayOffs.length ? (
            <ul className={css.dayOffList} data-testid="listener-hours-day-off-list">
              {dayOffs.map(row => {
                const when = formatDayOffWhen(row.startYmd, row.lastYmd, intl);
                const rowId = row.id || `${row.startYmd}-${row.lastYmd}`;
                const removing = dayOffDeleteId && dayOffDeleteId === row.id;
                return (
                  <li
                    key={rowId}
                    className={css.dayOffRow}
                    data-testid={`listener-hours-day-off-row-${rowId}`}
                    data-start={row.startYmd}
                    data-last={row.lastYmd}
                    data-weekly="false"
                  >
                    <div>
                      <span className={css.dayOffWhen}>{when}</span>
                      <span className={css.dayOffOnce}>
                        <FormattedMessage id="ListenerCalendar.dayOffOnce" />
                      </span>
                    </div>
                    {typeof onDeleteDayOff === 'function' && row.id ? (
                      <button
                        type="button"
                        className={css.dayOffRemove}
                        data-testid={`listener-hours-day-off-remove-${row.id}`}
                        disabled={!!removing}
                        aria-label={intl.formatMessage(
                          { id: 'ListenerCalendar.removeDayOffAria' },
                          { date: when }
                        )}
                        onClick={() => onDeleteDayOff(row.id)}
                      >
                        <FormattedMessage
                          id={
                            removing
                              ? 'ListenerCalendar.dayOffRemoving'
                              : 'ListenerCalendar.removeDayOff'
                          }
                        />
                      </button>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className={css.dayOffEmpty} data-testid="listener-hours-day-off-empty">
              <FormattedMessage id="ListenerCalendar.dayOffsEmpty" />
            </p>
          )}
        </section>
        ) : null}

        <div className={css.saveBar}>
          <button
            className={classNames(css.save, { [css.savePink]: saveTone === 'pink' })}
            type="submit"
            data-testid="listener-hours-save"
            data-save-tone={saveTone}
            disabled={inProgress}
          >
            <FormattedMessage id={inProgress ? 'ListenerCalendar.saving' : saveMessageId} />
          </button>
          {!simple ? (
            <button
              type="button"
              className={css.takeOff}
              data-testid="listener-hours-day-off"
              onClick={() => setDayOffOpen(true)}
            >
              <CalendarOffIcon />
              <FormattedMessage id="ListenerCalendar.takeADayOff" />
            </button>
          ) : null}
        </div>
      </form>

      {!simple && dayOffOpen ? (
        <div
          className={css.sheetScrim}
          role="presentation"
          onClick={() => setDayOffOpen(false)}
          data-testid="listener-hours-day-off-scrim"
        >
          <div
            className={css.sheet}
            role="dialog"
            aria-modal="true"
            aria-labelledby="take-a-day-off-title"
            data-testid="listener-hours-day-off-modal"
            onClick={event => event.stopPropagation()}
          >
            <ChangeOneDayEditor
              timeZone={zone}
              entries={entriesFromListenerDays(days)}
              inProgress={exceptionInProgress}
              error={exceptionError}
              onCancel={() => setDayOffOpen(false)}
              onSubmit={payload => {
                if (typeof onSaveException !== 'function') {
                  setDayOffOpen(false);
                  return undefined;
                }
                return Promise.resolve(onSaveException(payload))
                  .then(() => {
                    const toast = dayOffWhenFromExceptions(payload, zone, intl);
                    const isDayOff = oneOffDayOffsFromExceptions(payload, zone).length > 0;
                    setDayOffOpen(false);
                    setDayOffToast({ ...toast, kind: isDayOff ? 'off' : 'hours' });
                  })
                  .catch(() => {});
              }}
            />
          </div>
        </div>
      ) : null}

      {dayOffToast?.when ? (
        <div
          className={css.scrim}
          role="presentation"
          onClick={() => setDayOffToast(null)}
          data-testid="listener-hours-day-off-popup"
        >
          <div
            className={css.modal}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="day-off-saved-title"
            data-testid="listener-hours-day-off-saved"
            onClick={event => event.stopPropagation()}
          >
            <p id="day-off-saved-title" className={css.modalTitle}>
              <FormattedMessage
                id={
                  dayOffToast.kind === 'hours'
                    ? 'ListenerCalendar.hoursSaved'
                    : dayOffToast.isRange
                    ? 'ListenerCalendar.dayOffSavedRange'
                    : 'ListenerCalendar.dayOffSaved'
                }
                values={{ date: dayOffToast.when }}
              />
            </p>
            <button
              type="button"
              className={classNames(css.modalApply, css.modalOk)}
              data-testid="listener-hours-day-off-saved-ok"
              onClick={() => setDayOffToast(null)}
            >
              <FormattedMessage id="ListenerCalendar.dayOffSavedOk" />
            </button>
          </div>
        </div>
      ) : null}

      {copyOpen ? (
        <div
          className={css.scrim}
          role="presentation"
          onClick={() => setCopyOpen(false)}
          data-testid="listener-hours-copy-scrim"
        >
          <div
            className={css.modal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="copy-hours-title"
            data-testid="listener-hours-copy-modal"
            onClick={event => event.stopPropagation()}
          >
            <p id="copy-hours-title" className={css.modalTitle}>
              <FormattedMessage
                id="ListenerCalendar.copyConfirm"
                values={{ range: copyRange, day: copyDayName }}
              />
            </p>
            <div className={css.modalActions}>
              <div className={css.modalPrimaryRow}>
                <button type="button" className={css.modalCancel} onClick={() => setCopyOpen(false)}>
                  <FormattedMessage id="ListenerCalendar.copyConfirmCancel" />
                </button>
                <button
                  type="button"
                  className={css.modalApply}
                  data-testid="listener-hours-copy-all"
                  onClick={() => confirmCopy(false)}
                >
                  <FormattedMessage id="ListenerCalendar.copyConfirmApply" />
                </button>
              </div>
              <button
                type="button"
                className={css.modalWeekdays}
                data-testid="listener-hours-copy-weekdays"
                onClick={() => confirmCopy(true)}
              >
                <FormattedMessage id="ListenerCalendar.copyWeekdays" />
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ListenerHoursEditor;
